import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Container,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import { 
  Share, 
  ContentCopy, 
  Refresh, 
  Public,
  Visibility,
  Launch,
  Info
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  useGeneratePublicToken, 
  useCurrentPublicToken,
  useCopyInviteUrl
} from '../hooks/useInvitations';

const PublicAccessManagement: React.FC = () => {
  const { organization } = useAuth();
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hooks
  const generateTokenMutation = useGeneratePublicToken();
  const { data: currentToken, isLoading: tokenLoading, error: tokenError } = useCurrentPublicToken();
  const { copyPublicUrl } = useCopyInviteUrl();

  const handleGenerateToken = async () => {
    try {
      const result = await generateTokenMutation.mutateAsync();
      
      setSuccess('公開URLを生成しました');
      setShowRegenerateDialog(false);
      
      // クリップボードに公開URLをコピー
      if (result.token) {
        await copyPublicUrl(result.token);
        setSuccess(prev => prev + '\n公開URLをクリップボードにコピーしました');
      }
      
      // 成功メッセージを5秒後に消す
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || '公開URLの生成に失敗しました');
    }
  };

  const handleCopyUrl = async () => {
    if (!currentToken?.token) return;
    
    try {
      const copied = await copyPublicUrl(currentToken.token);
      if (copied) {
        setSuccess('公開URLをクリップボードにコピーしました');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('コピーに失敗しました');
      }
    } catch (err: any) {
      setError('コピーに失敗しました');
    }
  };

  const handleOpenUrl = () => {
    if (!currentToken?.url) return;
    window.open(currentToken.url, '_blank');
  };

  const formatAccessCount = (count: number) => {
    if (count === 0) return '0回';
    if (count === 1) return '1回';
    return `${count}回`;
  };

  const getPublicUrl = () => {
    if (!currentToken?.token) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/${currentToken.token}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          住民向け公開設定
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {organization?.name || 'マンション管理組合'} の住民向け公開URL管理
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* 公開URL管理カード */}
        <Box sx={{ flex: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  公開URL管理
                </Typography>
                {currentToken?.exists && (
                  <Chip 
                    label="アクティブ" 
                    color="success" 
                    size="small" 
                    icon={<Public />} 
                  />
                )}
              </Box>

              {tokenLoading ? (
                <Typography>読み込み中...</Typography>
              ) : tokenError ? (
                <Alert severity="error">
                  公開URL情報の取得に失敗しました
                </Alert>
              ) : currentToken?.exists ? (
                <Box>
                  {/* 公開URL表示 */}
                  <TextField
                    fullWidth
                    label="公開URL"
                    value={getPublicUrl()}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="URLをコピー">
                            <IconButton onClick={handleCopyUrl}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="新しいタブで開く">
                            <IconButton onClick={handleOpenUrl}>
                              <Launch />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  {/* アクセス統計 */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      アクセス統計
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Visibility sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          総アクセス数: {formatAccessCount(currentToken?.accessCount || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* アクションボタン */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyUrl}
                    >
                      URLをコピー
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Launch />}
                      onClick={handleOpenUrl}
                    >
                      プレビュー
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<Refresh />}
                      onClick={() => setShowRegenerateDialog(true)}
                    >
                      URL再生成
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Public sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    公開URLが設定されていません
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    住民がダッシュボードにアクセスするための公開URLを生成してください
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Share />}
                    onClick={handleGenerateToken}
                    disabled={generateTokenMutation.isPending}
                  >
                    {generateTokenMutation.isPending ? '生成中...' : '公開URLを生成'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* 使用方法ガイド */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1 }} />
                使用方法
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  公開URLについて
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • URLを知っている人なら誰でもアクセス可能
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • 読み取り専用でダッシュボードを表示
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • 有効期限なし（再生成まで有効）
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  セキュリティ
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • ランダムなトークンで保護
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • 必要に応じてURL再生成可能
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • アクセス履歴を記録
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  住民への共有方法
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  1. 「URLをコピー」でクリップボードにコピー
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  2. メールやお知らせで住民に配布
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3. 必要に応じてQRコードとして印刷配布
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* URL再生成確認ダイアログ */}
      <Dialog
        open={showRegenerateDialog}
        onClose={() => setShowRegenerateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>公開URL再生成の確認</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              公開URLを再生成すると、現在のURLは無効になります。
              既にURLを配布している場合は、新しいURLを住民に再度お知らせする必要があります。
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            本当に公開URLを再生成しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegenerateDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleGenerateToken}
            disabled={generateTokenMutation.isPending}
            startIcon={<Refresh />}
          >
            {generateTokenMutation.isPending ? '生成中...' : '再生成する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PublicAccessManagement;